import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Fingerprint, Loader2, XCircle } from 'lucide-react';
import { checkBiometrySettings, fetchBiometrySettings, updateBiometrySettings } from '../../api';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useToast } from '../../hooks/use-toast';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  buildBiometryPayload,
  describeCheckResult,
  draftFromSettings,
  isBiometryDraftDirty,
  tokenPlaceholder,
  validateBiometryDraft
} from './biometrySettings';

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

/**
 * Подключение к сервису биометрии: определение сотрудника по голосу — отдельный шаг конвейера
 * между распознаванием речи и анализом. Опция платная, по умолчанию выключена.
 */
const BiometryCard = ({ user }) => {
  const { toast } = useToast();
  const { refreshFeatures } = useFeatures();
  const isAdmin = user?.role === 'admin';
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(() => draftFromSettings(null));
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    fetchBiometrySettings()
      .then((settings) => {
        if (!cancelled) {
          setSaved(settings);
          setDraft(draftFromSettings(settings));
          setLoadError(null);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setLoadError(describeError(error));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const updateDraft = useCallback((patch) => {
    setDraft((current) => ({ ...current, ...patch }));
    setCheckResult(null);
  }, []);

  const validationError = validateBiometryDraft(draft, saved);
  const isDirty = isBiometryDraftDirty(draft, saved);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const updated = await updateBiometrySettings(buildBiometryPayload(draft));
      setSaved(updated);
      setDraft(draftFromSettings(updated));
      await refreshFeatures();
      toast({ title: updated?.enabled ? 'Биометрия включена' : 'Настройки биометрии сохранены' });
    } catch (error) {
      toast({
        title: 'Не удалось сохранить настройки биометрии',
        description: describeError(error),
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  }, [draft, refreshFeatures, toast]);

  const handleCheck = useCallback(async () => {
    setIsChecking(true);
    try {
      setCheckResult(await checkBiometrySettings(buildBiometryPayload(draft)));
    } catch (error) {
      setCheckResult({ ok: false, error: describeError(error) });
    } finally {
      setIsChecking(false);
    }
  }, [draft]);

  if (!isAdmin) {
    return null;
  }

  const check = describeCheckResult(checkResult);
  const isEnabled = Boolean(saved?.enabled);

  return (
    <Card role="region" aria-label="Биометрия оператора">
      <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" />
            Биометрия оператора
            <Badge variant={isEnabled ? 'default' : 'outline'}>{isEnabled ? 'Включена' : 'Выключена'}</Badge>
          </CardTitle>
          <CardDescription>
            Определение сотрудника по голосу после распознавания речи. Отдельная платная опция:
            без неё звонки обрабатываются как раньше.
          </CardDescription>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="biometry-enabled"
            data-ev="settings.biometry.enabled_toggle"
            checked={draft.enabled}
            disabled={isLoading || isSaving}
            onCheckedChange={(enabled) => updateDraft({ enabled })}
          />
          <Label htmlFor="biometry-enabled">Включить</Label>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {loadError && <p className="text-sm text-destructive">Не удалось загрузить настройки: {loadError}</p>}

        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="biometry-url">Адрес сервиса</Label>
            <Input
              id="biometry-url"
              data-ev="settings.biometry.url_edit"
              value={draft.baseUrl}
              placeholder="https://biometry.example.ru/"
              disabled={isLoading}
              onChange={(event) => updateDraft({ baseUrl: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="biometry-token">Токен доступа</Label>
            <Input
              id="biometry-token"
              data-ev="settings.biometry.token_edit"
              type="password"
              autoComplete="off"
              value={draft.token}
              placeholder={tokenPlaceholder(saved)}
              disabled={isLoading}
              onChange={(event) => updateDraft({ token: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="biometry-model">Модель (набор голосов)</Label>
            <Input
              id="biometry-model"
              data-ev="settings.biometry.model_edit"
              value={draft.model}
              placeholder="по умолчанию — модель токена"
              disabled={isLoading}
              onChange={(event) => updateDraft({ model: event.target.value })}
            />
          </div>
        </div>

        {validationError && <p className="text-sm text-destructive">{validationError}</p>}

        {check && (
          <div
            className={`rounded-md border p-3 text-sm ${check.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100' : 'border-destructive/40 bg-destructive/5 text-destructive'}`}
            role="status"
          >
            <p className="flex items-center gap-2 font-medium">
              {check.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
              {check.title}
            </p>
            {check.details.map((line) => (
              <p key={line} className="mt-1">
                {line}
              </p>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            data-ev="settings.biometry.check"
            variant="outline"
            disabled={isLoading || isChecking || !draft.baseUrl.trim()}
            onClick={handleCheck}
          >
            {isChecking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Проверить подключение
          </Button>
          <Button
            type="button"
            data-ev="settings.biometry.save"
            disabled={isLoading || isSaving || !isDirty || Boolean(validationError)}
            onClick={handleSave}
          >
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Сохранить
          </Button>
        </div>

        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            Модель — набор голосов сотрудников тенанта в сервисе биометрии. Если токену доступна одна
            модель, поле можно оставить пустым.
          </p>
          <p>
            Сотрудник от телефонии приоритетнее: биометрия назначает сотрудника только звонкам, где
            телефония его не определила, и отмечает расхождения. Новые сотрудники появляются в
            справочнике по ответам сервиса; если голос не узнан, звонок относится к карточке
            «Не определён (биометрия)».
          </p>
          <p>
            Если сервис недоступен или настроен неверно, задача останавливается с ошибкой
            «Биометрия: …». После исправления перезапустите её «с чекпоинта» во вкладке «Задания»:
            распознавание речи не повторится. Кнопка «Повторить биометрию» там же заново определяет
            сотрудника без повторного анализа — например, после загрузки новых эталонов.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default BiometryCard;
